import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { CheckCircle2Icon, RocketIcon, SettingsIcon, StarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui/button.tsx';
import { WorkspaceStepsInfoProcessor } from '@r/utils/workspaceSteps/workspace-steps-info-processor.ts';
import { format } from 'date-fns';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useUser } from '@r/api/auth.tsx';
import { useBalance } from '@r/context/balance-context.ts';
import CountUp from 'react-countup';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import {
  isTargetLogicWorkspace,
  isLinearLogicWorkspace,
  isStepLogicWorkspace
} from '@r/utils/workspace-utils.ts';
import { cn } from '@r/utils/utils.ts';
import type { LogicTypeStepWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';

type WorkspaceStep = LogicTypeStepWorkspaceReadModel;

export const Account = () => {
  const { t } = useTranslation();

  const user = useUser().data;
  const { workspace, isWorkspacePending, isWorkspaceError, isWorkspaceSuccess } =
    UseWorkspace(user);

  const { balance } = useBalance();

  let workspaceSteps: WorkspaceStep[] = [];
  let nextStep = null;
  let isLastStepReached = false;

  if (isWorkspaceSuccess && workspace && user) {
    if (isStepLogicWorkspace(workspace)) {
      const {
        workspaceSteps: getWorkspaceSteps,
        nextStep: getNextStep,
        isLastStepReached: getIsLastStepReached
      } = WorkspaceStepsInfoProcessor(workspace, user);
      workspaceSteps = getWorkspaceSteps;
      nextStep = getNextStep;
      isLastStepReached = getIsLastStepReached;
    }
  }

  const pointsAmount = user?.visibleWorkspaces?.[0]?.company?.balance || 0;
  const targetStatuses = user?.visibleWorkspaces?.[0]?.company?.targetStatuses || [];
  const achievedCount = targetStatuses.filter((status) => status.achievedAt !== null).length;
  const totalTargetStatuses = targetStatuses ? targetStatuses.length : 0;
  const registeredAt = user?.visibleWorkspaces?.[0]?.registeredAt || '';

  return (
    <div className="overflow-x-hidden flex-grow">
      <div className="pl-8 flex flex-col h-full text-foreground">
        <div className="mb-10 title">
          <h1 className="mb-3 flex items-center justify-center text-center font-bold text-2xl md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
            {t('account.welcome.title')} {user?.firstname}
          </h1>
          <p className="font-light text-center text-muted-foreground">
            {t('account.welcome.description')}
          </p>
        </div>
        <div className="space-y-7">
          <Card className="flex overflow-hidden flex-col justify-center w-full text-center border-0">
            <CardHeader className="flex flex-row justify-center items-center space-y-0 text-foreground">
              <SettingsIcon className="mr-3" />

              <CardTitle className="flex items-center font-bold text-start text-base sm:text-xl md:text-3xl">
                {t('account.personnal.informations.title')}
              </CardTitle>
            </CardHeader>

            <CardContent className="overflow-hidden px-8 text-start md:px-20">
              <p className="flex flex-col mb-2 sm:flex-row">
                {`${t('account.personnal.informations.name.label')} : `}
                <span className="font-bold text-foreground sm:ml-2">
                  {user?.lastname} {user?.firstname}
                </span>
              </p>

              <p className="flex flex-col mb-2 sm:flex-row">
                {`${t('account.personnal.informations.email.label')} : `}
                <span className="font-bold text-foreground sm:ml-2">{user?.email}</span>
              </p>

              {registeredAt && (
                <p className="flex flex-col mb-2 sm:flex-row">
                  {`${t('account.personnal.informations.inscription.date.label')} : `}
                  <span className="font-bold text-foreground sm:ml-2">
                    {format(new Date(registeredAt as string), 'dd-MM-yyyy')}
                  </span>
                </p>
              )}
            </CardContent>

            <CardFooter className="flex justify-center md:justify-start">
              <LinkProtected to="/personal-informations">
                <Button rounded="full" className="w-full" type="submit">
                  {t('account.personnal.informations.link.label')}
                </Button>
              </LinkProtected>
            </CardFooter>
          </Card>

          <Card className="flex flex-col justify-center w-full text-center border-0">
            <CardHeader className="flex flex-row justify-center items-center space-y-0 text-foreground">
              <StarIcon className="mr-3" />
              <CardTitle className="flex items-center font-bold text-start text-base sm:text-xl md:text-3xl">
                {t('account.points.title')}
              </CardTitle>
            </CardHeader>

            <CardContent className="flex flex-wrap justify-center text-start text-foreground">
              {(isWorkspacePending || isWorkspaceError) && (
                <div className="flex flex-wrap justify-center w-full">
                  <div className="mb-7 w-full text-center md:mb-0 md:w-6/12">
                    <div className="flex justify-center items-center mb-2">
                      <Skeleton className="w-4/12 h-7" />
                    </div>
                    <div className="mb-7 md:mb-0">
                      <Skeleton className="mx-auto w-6/12 h-14" />
                    </div>
                  </div>
                  <div className="w-full md:w-6/12">
                    <div className="flex justify-center items-center mb-2">
                      <Skeleton className="w-4/12 h-7" />
                    </div>
                    <div>
                      <Skeleton className="mx-auto w-6/12 h-14" />
                    </div>
                  </div>
                </div>
              )}
              {((isStepLogicWorkspace(workspace) && workspaceSteps) ||
                isLinearLogicWorkspace(workspace)) && (
                <div className="flex flex-wrap justify-center w-full">
                  <div
                    className={`mb-7 w-full text-center md:mb-0 md:w-6/12${
                      isStepLogicWorkspace(workspace) &&
                      !isLastStepReached &&
                      ' border-primary border-b-2 md:border-r-2 md:border-b-0'
                    }`}
                  >
                    <div className="flex justify-center items-center mb-2 font-bold">
                      <CheckCircle2Icon className="mr-3" />
                      {t('account.points.balance.label')}
                    </div>
                    <div className="mb-7 text-3xl font-bold md:mb-0">
                      {pointsTranslation(t, balance, true)}
                    </div>
                  </div>
                  {isStepLogicWorkspace(workspace) && !isLastStepReached && (
                    <div className="w-full text-center md:w-6/12">
                      <div className="flex justify-center items-center mb-2 font-bold">
                        <RocketIcon className="mr-3" />
                        {t('account.points.next.logic.step')}
                      </div>
                      <div className="text-3xl font-bold">
                        {pointsTranslation(t, nextStep?.stepPoint, true)}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isTargetLogicWorkspace(workspace) && (
                <div className="flex flex-wrap justify-center w-full">
                  <div className="mb-7 w-full text-center md:mb-0 md:w-6/12">
                    <div className="flex justify-center items-center mb-2 font-bold">
                      <CheckCircle2Icon className="mr-3" />
                      {t('account.points.balance.label')}
                    </div>
                    <div className="mb-7 text-3xl font-bold md:mb-0">
                      <CountUp
                        className="mb-7 text-3xl font-bold md:mb-0"
                        end={pointsAmount}
                        duration={2}
                        formattingFn={(n) =>
                          String(FormattedNumber({ value: n, style: 'decimal' }))
                        }
                        suffix={` ${pointsTranslation(t, pointsAmount)}`}
                      />
                    </div>
                  </div>

                  <div
                    className={cn(
                      'w-full text-center md:w-6/12',
                      totalTargetStatuses === 0 && 'hidden'
                    )}
                  >
                    <div className="flex justify-center items-center mb-2 font-bold">
                      <RocketIcon className="mr-3" />
                      {t('account.target.achieved.label')}
                    </div>
                    <div className="text-3xl font-bold">
                      {achievedCount}/{totalTargetStatuses}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex justify-center md:justify-start">
              <LinkProtected to="/performance">
                <Button rounded="full" className="w-full" type="submit">
                  {t('account.points.link.label')}
                </Button>
              </LinkProtected>
            </CardFooter>
          </Card>

          <Card className="flex flex-col justify-center w-full text-center border-0 bg-foreground">
            <CardContent className="flex flex-wrap justify-center p-8 space-y-4 w-full text-foreground md:p-16">
              <h1 className="mb-3 w-full text-2xl font-medium text-center text-primary-foreground">
                {t('account.section.any.question', { pointName: t('point.name_other') })}
              </h1>
              <LinkProtected to="/contact">
                <Button
                  variant="outline"
                  rounded="full"
                  className="m-auto font-bold text-foreground"
                >
                  {t('account.contact.link')}
                </Button>
              </LinkProtected>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
